<script setup lang="ts">
  import JSONEditor from 'jsoneditor'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { Button, message } from 'ant-design-vue'
  import { CheckCircle2 } from 'lucide-vue-next'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { PlotSchema } from '../models/PlotSchema'
  import { PlotJsonEditorSchema } from '../services/PlottingJsonPlotConfigurationService'
  import { PlottingService } from '../services/PlottingService'
  import { PlottingTypeGuardService } from '../services/PlottingTypeGuardService'

  interface IJsonPlotConfigurationProps {
    plotSchemas: Array<PlotSchema>
    availableColumns: Array<{ value: string; label: string }>
    modelValue: {}
    placeHolder?: string
    configurationLevel: number
    editorSchema: PlotJsonEditorSchema
    allowTypeChange: boolean
  }

  interface IJsonPlotConfigurationEmits {
    (e: 'update:modelValue', value: {}): void
    (e: 'plot-type-changed', type: string, configuration: {}): void
  }

  const props = defineProps<IJsonPlotConfigurationProps>()
  const emit = defineEmits<IJsonPlotConfigurationEmits>()

  const modelValue = ref<{}>({ ...props.modelValue })

  watch(
    modelValue,
    (newVal) => {
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  const newApplyablePlotType = ref<string | null>(null)

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        schema: props.editorSchema,
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          try {
            const newVal = editor.get()
            if (props.allowTypeChange === true) {
              onEditorChangeForPlot(newVal)
            } else {
              onEditorChangeForField(newVal)
            }
          } catch (err: any) {
            const editorValue = editor.getText?.() ?? ''
            if (editorValue.trim() === '') {
              message.error('Editor content cannot be empty.', defaultMessageDurationSeconds)
            }
          }
        }
      })
      setJSONEditorModel(modelValue.value)
    }
  }

  const onEditorChangeForPlot = (editorValue: any) => {
    newApplyablePlotType.value = null
    if (typeof editorValue === 'object') {
      if (editorValue.type != null) {
        const plotType = PlottingService.tryGetPlotSchemaByType(editorValue.type, props.plotSchemas)
        if (
          plotType != null &&
          plotType.type !== PlottingService.CUSTOM_PLOT_TYPE &&
          (props.configurationLevel === 1 || !PlottingTypeGuardService.supportsChildPlots(plotType))
        ) {
          newApplyablePlotType.value = plotType.type
        }
      }
      modelValue.value = editorValue
    }
  }

  const onEditorChangeForField = (editorValue: any) => {
    modelValue.value = editorValue
  }

  const setJSONEditorModel = (value: {}) => {
    if (editor != null) {
      newApplyablePlotType.value = null
      editor.set(value)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  const onApplyNewPlotType = () => {
    if (newApplyablePlotType != null) {
      emit('plot-type-changed', newApplyablePlotType.value as string, modelValue)
    }
  }

  onMounted(() => {
    initializeJSONEditor()
  })

  onUnmounted(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<template>
  <div class="flex flex-col">
    <div v-if="props.allowTypeChange === true" class="mb-3 flex justify-end">
      <a-tooltip>
        <template #title>
          {{ `Apply Rule type: ${newApplyablePlotType}` }}
        </template>
        <Button type="primary" :disabled="newApplyablePlotType == null" @click="onApplyNewPlotType">
          <span class="ant-button-flex-content">
            <CheckCircle2 class="h-4 w-4" />
            {{ 'Apply Rule Type' }}
          </span>
        </Button>
      </a-tooltip>
    </div>
    <div ref="editorContainer" style="min-height: 500px; height: 500px"></div>
  </div>
</template>
